import type { VocalLib } from '@tunasong/music-lib'

/**
 * The two-take range calibration (§5.8), as a pure reducer.
 *
 * It takes the *result* of `VocalLib`-backed pitch measurement — a MIDI number, or `null` when
 * nothing was heard — so no frames and no audio reach it, and every rule below is testable.
 *
 * Two rules earn their place here rather than in the panel:
 *  - `null` never advances the step. A range built from a cough would push every later exercise
 *    into a tessitura the singer never agreed to, and nothing downstream could tell.
 *  - A highest note at or below the lowest is rejected. It collapses the middle 60 % of the
 *    range to a single pitch, which makes every exercise unsingable in a way that looks like a
 *    scoring bug rather than a calibration one.
 *
 * Spec: docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §5.8, §9
 */

export type CalibrationStep = 'lowest' | 'highest' | 'done'

export interface CalibrationState {
  step: CalibrationStep
  /** Fractional MIDI. Null until the take is accepted. */
  low: number | null
  high: number | null
  /** The last take was rejected — nothing heard, or not a range. Cleared by the next take. */
  nothingHeard: boolean
}

export const initialCalibration: CalibrationState = { step: 'lowest', low: null, high: null, nothingHeard: false }

export const recordCalibration = (state: CalibrationState, midi: number | null): CalibrationState => {
  if (state.step === 'done') {
    return state
  }
  if (midi === null) {
    return { ...state, nothingHeard: true }
  }
  if (state.step === 'lowest') {
    return { step: 'highest', low: midi, high: null, nothingHeard: false }
  }
  if (state.low === null || midi <= state.low) {
    return { ...state, high: null, nothingHeard: true }
  }
  return { step: 'done', low: state.low, high: midi, nothingHeard: false }
}

/** The stored range, or null while the calibration is unfinished. */
export const calibratedRange = (state: CalibrationState): VocalLib.VocalRange | null =>
  state.low !== null && state.high !== null ? { low: state.low, high: state.high } : null
